"use client";

import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { usePublicPathname } from "@/app/hooks/use-public-pathname";
import Container from "@/app/components/ui/container";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover";
import type { AlertT } from "@/app/types/alerts";
import type { TenantKeyT } from "@/app/config/tenant-registry";
import { useIsMobile } from "@/app/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { isBrandPath } from "@/app/config/brand-registry";

const DESKTOP_BREAKPOINT = 1024;

const BLUE_TENANT_KEYS = new Set<TenantKeyT>([
  "autohaus-rauch",
  "schuetz-gross-umstadt",
]);

type AlertBannerContentPropsT = {
  alert: AlertT;
  tenantKey: TenantKeyT;
};

/**
 * Alert Banner Content Component
 * Displays alert/banner based on display_mode:
 * - "banner": Yellow banner at top
 * - "alert": Card with bg-muted
 * - "both": Both banner and card
 * - "none": Nothing (handled in parent)
 */
export default function AlertBannerContent({
  alert,
  tenantKey,
}: AlertBannerContentPropsT) {
  const pathname = usePublicPathname();
  const isSkoda = isBrandPath(pathname, "skoda");
  const isAudi = isBrandPath(pathname, "audi");
  const isBlueTenant = BLUE_TENANT_KEYS.has(tenantKey);
  const isBelowDesktop = useIsMobile(DESKTOP_BREAKPOINT);
  const [isBannerOpen, setIsBannerOpen] = useState(true);
  const [isBannerClosing, setIsBannerClosing] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const popoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Banner component only handles banner display
  // Alert card is handled separately in alert-card.tsx

  const handleBannerClose = () => {
    setIsBannerClosing(true);
    setTimeout(() => {
      setIsBannerOpen(false);
    }, 300);
  };

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTruncated =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTextTruncated(isTruncated);
      }
    };

    // Check after a short delay to ensure layout is calculated
    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [isBannerOpen]);

  const handlePopoverMouseEnter = () => {
    if (popoverCloseTimeoutRef.current) {
      clearTimeout(popoverCloseTimeoutRef.current);
      popoverCloseTimeoutRef.current = null;
    }
    setIsPopoverOpen(true);
  };

  const handlePopoverMouseLeave = () => {
    popoverCloseTimeoutRef.current = setTimeout(() => {
      setIsPopoverOpen(false);
    }, 100);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (isBelowDesktop) {
      setIsPopoverOpen(open);
      return;
    }

    if (!open) {
      setIsPopoverOpen(false);
    }
  };

  const hoverPopoverHandlers = isBelowDesktop
    ? {}
    : {
        onMouseEnter: handlePopoverMouseEnter,
        onMouseLeave: handlePopoverMouseLeave,
      };

  useEffect(() => {
    return () => {
      if (popoverCloseTimeoutRef.current) {
        clearTimeout(popoverCloseTimeoutRef.current);
      }
    };
  }, []);

  // Combine title and description for banner display
  const textContent = alert.title
    ? alert.description
      ? `${alert.title} - ${alert.description}`
      : alert.title
    : alert.description || "";
  const hasContent = Boolean(textContent);

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {/* Banner Display (yellow banner at top) */}
      {isBannerOpen && (
        <div
          className={cn(
            "relative top-0 right-0 left-0 z-[101] bg-[#EFBD3E] text-white transition-all duration-300 ease-in-out",
            isBlueTenant && "bg-[#1B4C85] text-white",
            isSkoda && "bg-[#78FAAE] text-black",
            isAudi && "bg-[#262E39] text-white",
            isBannerClosing
              ? "max-h-0 overflow-hidden opacity-0"
              : "max-h-32 opacity-100"
          )}
        >
          <Container
            size="default"
            className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-4 py-1"
          >
            <div></div>
            {isTextTruncated ? (
              <Popover
                open={isPopoverOpen}
                onOpenChange={handlePopoverOpenChange}
              >
                <PopoverTrigger asChild>
                  <p
                    ref={textRef}
                    {...hoverPopoverHandlers}
                    className={cn(
                      "max-w-2xl cursor-pointer truncate text-center text-sm font-medium text-white hover:underline",
                      isSkoda && "text-black"
                    )}
                  >
                    {textContent}
                  </p>
                </PopoverTrigger>
                <PopoverContent
                  className="z-[105] w-[90vw] max-w-3xl sm:w-[80vw] md:w-[70vw]"
                  {...hoverPopoverHandlers}
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  <div className="space-y-2">
                    {alert.title && (
                      <h3 className="text-primary text-sm font-bold">
                        {alert.title}
                      </h3>
                    )}
                    {alert.description && (
                      <p className="text-primary text-sm font-light whitespace-pre-wrap">
                        {alert.description}
                      </p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <p
                ref={textRef}
                className={cn(
                  "max-w-2xl truncate text-center text-sm font-medium text-white",
                  isSkoda && "text-black"
                )}
              >
                {textContent}
              </p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleBannerClose}
                className="flex-shrink-0 rounded-sm p-2 transition-colors"
                aria-label="Banner schließen"
              >
                <X
                  className={cn("h-4 w-4 text-white", isSkoda && "text-black")}
                />
              </button>
            </div>
          </Container>
        </div>
      )}
    </>
  );
}
